import React, { useEffect, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import BulletList from "@tiptap/extension-bullet-list";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Document from "@tiptap/extension-document";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import TableRow from "@tiptap/extension-table-row";
import Table from "@tiptap/extension-table";
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Mathematics } from '@tiptap-pro/extension-mathematics'
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    StrikethroughS,
    Superscript as SuperscriptIcon,
    Subscript as SubscriptIcon,
    FormatAlignLeft,
    FormatAlignCenter,
    FormatAlignRight,
    FormatListBulleted,
    FormatListNumbered,
    Undo,
    Redo,
    InsertLink,
    Image as ImageIcon,
    TableChart,
    FormatQuote,
    FormatIndentDecrease,
    FormatIndentIncrease,
    ArrowDropDown,
} from "@mui/icons-material";
import FunctionsIcon from '@mui/icons-material/Functions';
import katex from 'katex';
import 'katex/dist/katex.min.css'
// import { ModalComponent } from "./Modal";
// import { TextAreaComponent } from "./TextArea";
// import { ButtonComponent } from "./Button";
import {
    ExpandMore as ExpandMoreIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import { ColorPicker, FontSize, TextColor, TextSizeDropdown } from "./textStyling";
import TextStyle from "@tiptap/extension-text-style";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import { TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Tooltip,
    useTheme,
    useMediaQuery,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Grid2,
} from '@mui/material';

// LaTeX Modal Component
const LatexModalContent = ({ latexContent, setLatexContent }) => {
    const [previewHtml, setPreviewHtml] = useState('');
    const [openSections, setOpenSections] = useState({});
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        try {
            const renderedLatex = katex.renderToString(latexContent, {
                throwOnError: false,
                displayMode: true
            });
            setPreviewHtml(renderedLatex);
        } catch (error) {
            setPreviewHtml('<span style="color: #f44336;">Invalid LaTeX</span>');
        }
    }, [latexContent]);

    const categorizedExamples = {
        "Basic Arithmetic (Class 1–5)": [
            { label: 'Addition', value: '5 + 3 = 8' },
            { label: 'Subtraction', value: '9 - 4 = 5' },
            { label: 'Multiplication', value: '6 \\times 3 = 18' },
            { label: 'Division', value: '12 \\div 4 = 3' },
            { label: 'Fraction', value: '\\frac{a}{b}' },
        ],
        "Geometry & Algebra (Class 6–10)": [
            { label: 'Exponent', value: 'a^2 + b^2 = c^2' },
            { label: 'Square Root', value: '\\sqrt{x}' },
            { label: 'Angle', value: '\\angle ABC = 90^\\circ' },
            { label: 'Perimeter of Square', value: 'P = 4a' },
            { label: 'Area of Circle', value: 'A = \\pi r^2' },
            { label: 'Quadratic Formula', value: 'x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}' },
            { label: 'Linear Equation', value: 'y = mx + c' },
            { label: 'Set Notation', value: 'A = \\{x \\in \\mathbb{N} \\mid x < 10\\}' },
            { label: 'Probability', value: 'P(E) = \\frac{n(E)}{n(S)}' },
        ],
        "Advanced Math (Class 11–12)": [
            { label: 'Summation', value: '\\sum_{i=1}^{n} i^2' },
            { label: 'Integral', value: '\\int_{a}^{b} f(x) \\, dx' },
            { label: 'Differentiation', value: '\\frac{dy}{dx}' },
            { label: 'Matrix', value: '\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}' },
            { label: 'Determinant', value: '\\begin{vmatrix} a & b \\\\ c & d \\end{vmatrix}' },
            { label: 'Greek Letters', value: '\\alpha, \\beta, \\theta, \\lambda' }
        ]
    };

    const insertExample = (example) => {
        setLatexContent(prev => {
            const textarea = document.getElementById('latex-textarea');
            const cursorPosition = textarea?.selectionStart || prev.length;
            return prev.substring(0, cursorPosition) + example + prev.substring(cursorPosition);
        });
    };

    const handleClear = () => {
        setLatexContent('');
    };

    return (
        <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
            {/* LaTeX Input Section */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                }}>
                    <Typography variant="subtitle2" component="label" htmlFor="latex-textarea">
                        LaTeX Expression
                    </Typography>
                    <Tooltip title="Clear LaTeX">
                        <span>
                            <IconButton
                                onClick={handleClear}
                                disabled={latexContent === ''}
                                size="small"
                                sx={{ p: 0.5 }}
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                <TextField
                    id="latex-textarea"
                    value={latexContent}
                    onChange={(e) => setLatexContent(e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                    variant="outlined"
                    placeholder="Enter LaTeX expression (without $ symbols)"
                    size="small"
                />
            </Box>

            {/* Preview Section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" component="label" sx={{ mb: 1, display: 'block' }}>
                    Preview
                </Typography>
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        minHeight: 64,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'grey.50',
                        '& .katex-display': {
                            margin: 0
                        }
                    }}
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
            </Box>

            {/* Examples Section */}
            <Box>
                <Typography variant="subtitle2" component="label" sx={{ mb: 2, display: 'block' }}>
                    Examples
                </Typography>
                <Box sx={{ maxHeight: isMobile ? 300 : 400, overflowY: 'auto' }}>
                    {Object.entries(categorizedExamples).map(([category, examples]) => (
                        <Accordion
                            key={category}
                            sx={{
                                mb: 1,
                                '&:before': { display: 'none' },
                                boxShadow: 1
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    backgroundColor: 'grey.100',
                                    '&:hover': {
                                        backgroundColor: 'grey.200'
                                    },
                                    '& .MuiAccordionSummary-content': {
                                        margin: '8px 0'
                                    }
                                }}
                            >
                                <Typography variant="body2" fontWeight="medium">
                                    {category}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 2, backgroundColor: 'background.paper' }}>
                                <Grid2 container spacing={1}>
                                    {examples.map((example, index) => (
                                        <Grid2
                                            item
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            key={index}
                                        >
                                            <Button
                                                onClick={() => insertExample(example.value)}
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                sx={{
                                                    justifyContent: 'flex-start',
                                                    textAlign: 'left',
                                                    p: 1,
                                                    minHeight: 'auto',
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.75rem',
                                                    textTransform: 'none',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    '&:hover': {
                                                        backgroundColor: 'grey.100'
                                                    }
                                                }}
                                                title={`${example.label}: ${example.value}`}
                                            >
                                                <Box component="span" sx={{
                                                    fontWeight: 'bold',
                                                    mr: 0.5,
                                                    color: 'primary.main'
                                                }}>
                                                    {example.label}:
                                                </Box>
                                                <Box component="span" sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.7rem',
                                                    color: 'text.secondary'
                                                }}>
                                                    {example.value}
                                                </Box>
                                            </Button>
                                        </Grid2>
                                    ))}
                                </Grid2>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export const CustomIconButton = ({ onClick, disabled, IconComponent, isActive, tooltip }) => {
    return (
        <Tooltip title={tooltip || ''} placement="top">
            <button
                onClick={onClick}
                disabled={disabled}
                className={`p-1 rounded ${isActive ? "bg-secondary" : ""
                    } hover:bg-gray-200`}
            >
                <IconComponent />
            </button>
        </Tooltip>
    );
};

const MenuBar = ({ editor, openLatexModal }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [showTableMenu, setShowTableMenu] = React.useState(false);
    const [tableMenuAnchor, setTableMenuAnchor] = React.useState(null);

    if (!editor) {
        return null;
    }

    const addTable = () => {
        editor.chain()
            .focus()
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .run();
    };

    const handleTableMenuOpen = (event) => {
        setTableMenuAnchor(event.currentTarget);
        setShowTableMenu(true);
    };

    const handleTableMenuClose = () => {
        setShowTableMenu(false);
        setTableMenuAnchor(null);
    };

    const TableMenu = () => (
        <Menu
            anchorEl={tableMenuAnchor}
            open={showTableMenu}
            onClose={handleTableMenuClose}
            PaperProps={{
                sx: {
                    minWidth: 200,
                    maxHeight: 400,
                }
            }}
        >
            <MenuItem onClick={() => {
                editor.chain().focus().addColumnBefore().run();
                handleTableMenuClose();
            }}>
                Add Column Before
            </MenuItem>
            <MenuItem onClick={() => {
                editor.chain().focus().addColumnAfter().run();
                handleTableMenuClose();
            }}>
                Add Column After
            </MenuItem>
            <MenuItem onClick={() => {
                editor.chain().focus().deleteColumn().run();
                handleTableMenuClose();
            }}>
                Delete Column
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => {
                editor.chain().focus().addRowBefore().run();
                handleTableMenuClose();
            }}>
                Add Row Before
            </MenuItem>
            <MenuItem onClick={() => {
                editor.chain().focus().addRowAfter().run();
                handleTableMenuClose();
            }}>
                Add Row After
            </MenuItem>
            <MenuItem onClick={() => {
                editor.chain().focus().deleteRow().run();
                handleTableMenuClose();
            }}>
                Delete Row
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => {
                editor.chain().focus().deleteTable().run();
                handleTableMenuClose();
            }}>
                Delete Table
            </MenuItem>
            <Divider />
            <MenuItem
                onClick={() => {
                    editor.chain().focus().mergeCells().run();
                    handleTableMenuClose();
                }}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    '& .helper-text': {
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        ml: 1
                    }
                }}
            >
                <span>Merge Cells</span>
                <span className="helper-text">Select cells first</span>
            </MenuItem>
            <MenuItem onClick={() => {
                editor.chain().focus().splitCell().run();
                handleTableMenuClose();
            }}>
                Unmerge Cells
            </MenuItem>
            <MenuItem onClick={() => {
                editor.chain().focus().toggleHeaderCell().run();
                handleTableMenuClose();
            }}>
                Toggle Header
            </MenuItem>
        </Menu>
    );

    // Custom IconButton component with active state styling
    const MenuIconButton = ({
        IconComponent,
        onClick,
        disabled = false,
        isActive = false,
        tooltip,
        ...props
    }) => (
        <Tooltip title={tooltip || ''} arrow>
            <span>
                <IconButton
                    onClick={onClick}
                    disabled={disabled}
                    size="small"
                    sx={{
                        backgroundColor: isActive ? 'action.selected' : 'transparent',
                        '&:hover': {
                            backgroundColor: isActive
                                ? 'action.selected'
                                : 'action.hover'
                        },
                        '&.Mui-disabled': {
                            color: 'action.disabled'
                        }
                    }}
                    {...props}
                >
                    <IconComponent fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    );

    return (
        <Paper
            elevation={1}
            sx={{
                width: '100%',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    minHeight: 48,
                    // Custom scrollbar styling
                    '&::-webkit-scrollbar': {
                        height: 6,
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'action.disabled',
                        borderRadius: 3,
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: 'action.hover',
                    },
                }}
            >
                {/* Undo and Redo Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MenuIconButton
                        IconComponent={Undo}
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}
                        tooltip="Undo"
                    />
                    <MenuIconButton
                        IconComponent={Redo}
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}
                        tooltip="Redo"
                    />
                </Box>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                {/* Tables and Attachments Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MenuIconButton
                            IconComponent={TableChart}
                            onClick={addTable}
                            tooltip="Insert Table"
                        />
                        {editor.isActive('table') && (
                            <IconButton
                                onClick={handleTableMenuOpen}
                                size="small"
                                sx={{ ml: -0.5 }}
                            >
                                <ArrowDropDown fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                    <MenuIconButton
                        IconComponent={FormatQuote}
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                        isActive={editor.isActive('blockquote')}
                        tooltip="Blockquote"
                    />
                </Box>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                {/* Text Styling Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextSizeDropdown editor={editor} />
                    {/* <ColorPicker editor={editor} /> */}
                </Box>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                {/* Text Formatting Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MenuIconButton
                        IconComponent={FormatBold}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        isActive={editor.isActive('bold')}
                        tooltip="Bold"
                    />
                    <MenuIconButton
                        IconComponent={FormatItalic}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        isActive={editor.isActive('italic')}
                        tooltip="Italic"
                    />
                    <MenuIconButton
                        IconComponent={FormatUnderlined}
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        isActive={editor.isActive('underline')}
                        tooltip="Underline"
                    />
                    <MenuIconButton
                        IconComponent={StrikethroughS}
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        isActive={editor.isActive('strike')}
                        tooltip="Strikethrough"
                    />
                    <MenuIconButton
                        IconComponent={SuperscriptIcon}
                        onClick={() => editor.chain().focus().toggleSuperscript().run()}
                        isActive={editor.isActive('superscript')}
                        tooltip="Superscript"
                    />
                    <MenuIconButton
                        IconComponent={SubscriptIcon}
                        onClick={() => editor.chain().focus().toggleSubscript().run()}
                        isActive={editor.isActive('subscript')}
                        tooltip="Subscript"
                    />
                </Box>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                {/* Alignment Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MenuIconButton
                        IconComponent={FormatAlignLeft}
                        onClick={() => editor.chain().focus().setTextAlign('left').run()}
                        isActive={editor.isActive({ textAlign: 'left' })}
                        tooltip="Align Left"
                    />
                    <MenuIconButton
                        IconComponent={FormatAlignCenter}
                        onClick={() => editor.chain().focus().setTextAlign('center').run()}
                        isActive={editor.isActive({ textAlign: 'center' })}
                        tooltip="Align Center"
                    />
                    <MenuIconButton
                        IconComponent={FormatAlignRight}
                        onClick={() => editor.chain().focus().setTextAlign('right').run()}
                        isActive={editor.isActive({ textAlign: 'right' })}
                        tooltip="Align Right"
                    />
                </Box>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                {/* Lists Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MenuIconButton
                        IconComponent={FormatListBulleted}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        isActive={editor.isActive('bulletList')}
                        tooltip="Bullet List"
                    />
                    <MenuIconButton
                        IconComponent={FormatListNumbered}
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        isActive={editor.isActive('orderedList')}
                        tooltip="Numbered List"
                    />
                </Box>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                {/* LaTeX Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MenuIconButton
                        IconComponent={FunctionsIcon}
                        onClick={openLatexModal}
                        tooltip="Insert LaTeX Math"
                    />
                </Box>
            </Box>

            {/* Table Menu Component */}
            <TableMenu />
        </Paper>
    );
};

const RichTextEditor = ({ initialContent, onChange, disabled = false, onTextSelection }) => {
    const isUserChange = React.useRef(false);
    const [isLatexModalOpen, setIsLatexModalOpen] = useState(false);
    const [latexContent, setLatexContent] = useState('');

    const openLatexModal = () => {
        setIsLatexModalOpen(true);
    };

    const closeLatexModal = () => {
        setIsLatexModalOpen(false);
        setLatexContent('');
    };

    const handleLatexSubmit = () => {
        // For now, just log the LaTeX content and close modal
        console.log('LaTeX content:', latexContent);

        // Copy LaTeX content to clipboard with $ wrapping
        const wrappedLatex = `$${latexContent}$`;
        navigator.clipboard.writeText(wrappedLatex)
            .then(() => {
                console.log('LaTeX content copied to clipboard');
            })
            .catch((err) => {
                console.error('Failed to copy LaTeX content:', err);
            });

        // TODO: Insert the LaTeX into the editor
        // editor.chain().focus().insertMath({ inline: false, content: latexContent }).run();

        closeLatexModal();
    };

    const editor = useEditor({
        editable: !disabled,
        extensions: [
            StarterKit.configure({
                paragraph: {
                    HTMLAttributes: {
                        class: 'paragraph',
                    },
                    addAttributes() {
                        return {
                            indent: {
                                default: 0,
                                renderHTML: attributes => ({
                                    style: `padding-left: ${attributes.indent * 2}em`,
                                }),
                                parseHTML: element => element.style.paddingLeft ?
                                    parseInt(element.style.paddingLeft) / 2 : 0,
                            },
                        };
                    },
                },
                bulletList: false,
                orderedList: false,
                listItem: false,
            }),
            Underline,
            BulletList.configure({
                HTMLAttributes: {
                    class: 'bullet-list',
                },
            }),
            OrderedList.configure({
                HTMLAttributes: {
                    class: 'ordered-list',
                },
            }),
            ListItem.configure({
                HTMLAttributes: {
                    class: 'list-item',
                },
            }),
            Text,
            TextStyle,
            FontSize, /* Custom Extension for font size */
            TextColor, /* Custom Extension for font color */
            Document,
            Superscript,
            Subscript,
            Table.configure({
                resizable: true,
                allowTableNodeSelection: true,
            }),
            TableRow,
            TableCell.configure({
                allowTableNodeSelection: true,
            }),
            TableHeader,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
                alignments: ['left', 'center', 'right'],
                defaultAlignment: 'left',
            }),
            Link.configure({
                openOnClick: true,
                linkOnPaste: true,
            }),
            Image.configure({
                inline: true,
            }),
            Mathematics,
        ],
        content: initialContent || "<p>Start typing...</p>",
        editorProps: {
            attributes: {
                class: "outline-none w-full h-full [&_.bullet-list]:list-disc [&_.ordered-list]:list-decimal [&_.list-item]:ml-4",
            },
        },
        onUpdate: ({ editor }) => {
            if (!disabled && onChange) {
                isUserChange.current = true;
                onChange(editor.getHTML());
            }
        },
    });

    React.useEffect(() => {
        if (editor && initialContent !== undefined && !isUserChange.current) {
            editor.commands.setContent(initialContent);
        }
        isUserChange.current = false;
    }, [editor, initialContent]);

    // We're attaching the mouseup event to the EditorContent component instead
    const handleMouseUp = () => {
        if (disabled || !onTextSelection) return;
        onTextSelection();
    };

    return (
        <div className="h-full flex flex-col">
            {!disabled && editor && (
                <div className="top-0 z-10 sticky">
                    <MenuBar editor={editor} openLatexModal={openLatexModal} />
                </div>
            )}
            <div
                className="border border-greyElement py-4 px-2 mt-1 rounded flex-1"
                id="editor-container"
                style={{
                    height: "calc(100% - 48px)",
                    minHeight: "200px",
                }}
            >
                <EditorContent
                    editor={editor}
                    className="h-full [&_.ProseMirror]:h-full [&_.ProseMirror]:outline-none [&_table]:border-collapse [&_table]:w-full [&_table]:border [&_table]:border-gray-300 [&_th]:border [&_th]:border-gray-300 [&_th]:bg-gray-50 [&_th]:p-2 [&_td]:border [&_td]:border-gray-300 [&_td]:p-2 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic"
                    onMouseUp={handleMouseUp}
                />
            </div>
            {/* Modal placed in RichTextEditor component instead of MenuBar */}
            <Dialog open={isLatexModalOpen} onClose={closeLatexModal} fullWidth maxWidth="sm">
                <DialogTitle>Mathematical Equations</DialogTitle>
                <DialogContent>
                    <LatexModalContent
                        latexContent={latexContent}
                        setLatexContent={setLatexContent}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeLatexModal}>Cancel</Button>
                    <Button onClick={handleLatexSubmit} variant="contained" color="primary">
                        Copy LaTeX
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default RichTextEditor;